import type { ReactNode } from "react";
import { cn } from "@/shared/lib/utils";
import { Chip } from "@/shared/ui/chip";
import { STATUS_LABEL } from "@/shared/lib/labels";
import type { ModerationStatus } from "@/shared/api/book";

/** Üres állapot: egy mondat `muted` + (opcionálisan) egy cselekvés (design-rendszer 6.). */
export function Empty({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-start gap-4 py-8">
      <p className="text-[15px] leading-[1.5] text-muted">{children}</p>
      {action}
    </div>
  );
}

/** Betöltés: a komponens körvonalát megtartó skeleton. */
export function Skeleton({ className }: { className?: string }) {
  return <div className={cn("animate-pulse rounded-md bg-gray", className)} aria-hidden />;
}

export function ListSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <div className="flex flex-col gap-3" role="status" aria-label="Betöltés">
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-16 w-full" />
      ))}
    </div>
  );
}

export function ErrorNote({ children }: { children?: ReactNode }) {
  return (
    <p role="alert" className="text-[13px] leading-[1.4] text-red">
      {children ?? "Valami hiba történt. Próbáld újra."}
    </p>
  );
}

/** Moderációra váró / elutasított saját elem jelölése. */
export function StatusChip({ status }: { status: ModerationStatus }) {
  if (status === "approved") return null;
  return (
    <Chip variant={status === "rejected" ? "danger" : "pending"}>{STATUS_LABEL[status]}</Chip>
  );
}

/** Lapozó a hosszabb listákhoz (az oldalszám az URL-ben él, így megosztható). */
export function Pager({
  page,
  total,
  pageSize,
  onChange,
}: {
  page: number;
  total: number;
  pageSize: number;
  onChange: (page: number) => void;
}) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  if (pages <= 1) return null;
  return (
    <nav aria-label="Lapozás" className="flex items-center justify-between gap-3 pt-2">
      <button
        type="button"
        disabled={page <= 1}
        onClick={() => onChange(page - 1)}
        className="h-11 rounded-md border border-line px-4 text-[13px] font-semibold disabled:opacity-40"
      >
        Előző
      </button>
      <span className="text-[13px] text-muted">
        {page}. oldal / {pages}
      </span>
      <button
        type="button"
        disabled={page >= pages}
        onClick={() => onChange(page + 1)}
        className="h-11 rounded-md border border-line px-4 text-[13px] font-semibold disabled:opacity-40"
      >
        Következő
      </button>
    </nav>
  );
}
